import { addImageParams } from '@/utils/urls';
import { DEFAULT_CARD_DIMENSIONS } from '../../constants/defaultCardDimensions.constant';
import {
    getItemFirstImageUrl,
    getItemModelTitle,
    getItemNumPhotos,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { useAppSelector } from '@/redux/hooks';
import { useItemOrPriceResultLink } from '@/hooks/useItemOrPriceResultLink';
import CardStatusBanner from '@/components/ItemCard/CardStatusBanner';
import styled, { css } from 'styled-components';

export type ItemCardImageProps = {
    alignBannerBottomWithGap?: boolean;
    className?: string;
    /** Both the height and width of image */
    dimension?: number;
    imageQuality?: number;
    itemId: number;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onNavigate?: () => void;
    sharpenImage?: boolean;
    shouldGiveRangeForMobileSize?: boolean;
    showBidStatus?: boolean;
    useInFluidGrid?: boolean;
};

export default function ItemCardImage({
    alignBannerBottomWithGap = false,
    className,
    dimension,
    imageQuality,
    itemId,
    onMouseEnter,
    onMouseLeave,
    onNavigate,
    sharpenImage,
    shouldGiveRangeForMobileSize,
    showBidStatus = false,
    useInFluidGrid = false,
}: ItemCardImageProps) {
    const title = useAppSelector((state) => getItemModelTitle(state, itemId));
    const itemUrl = useItemOrPriceResultLink(itemId, true);
    const numPhotos = useAppSelector((state) => getItemNumPhotos(state, itemId));
    const firstPhoto = useAppSelector((state) => getItemFirstImageUrl(state, itemId));
    const imageUrl = addImageParams({
        height: dimension ? dimension : DEFAULT_CARD_DIMENSIONS.desktop,
        quality: imageQuality,
        sharpen: sharpenImage,
        url: firstPhoto,
    });

    return (
        <ContainerLink
            $shouldGiveRangeForMobileSize={shouldGiveRangeForMobileSize}
            className={className}
            data-testid="itemCardImageLink"
            href={itemUrl}
            onClick={() => {
                onNavigate?.();
            }}
        >
            <ItemCardImageContainer
                $dimension={dimension}
                $shouldGiveRangeForMobileSize={shouldGiveRangeForMobileSize}
                $useInFluidGrid={useInFluidGrid}
                aria-label="Item image"
                data-testid="itemCardImage"
            >
                {showBidStatus && (
                    <CardStatusBanner
                        alignBannerBottomWithGap={alignBannerBottomWithGap}
                        alignBannerToBottom={!alignBannerBottomWithGap}
                        itemId={itemId}
                    />
                )}
                <StyledImage
                    $useInFluidGrid={useInFluidGrid}
                    alt={`${title} (1 of ${numPhotos})`}
                    height={dimension ? dimension : DEFAULT_CARD_DIMENSIONS.desktop}
                    loading="lazy"
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    src={imageUrl}
                    width={dimension ? dimension : DEFAULT_CARD_DIMENSIONS.desktop}
                />
            </ItemCardImageContainer>
        </ContainerLink>
    );
}

const ContainerLink = styled(Link)<{ $shouldGiveRangeForMobileSize: boolean }>`
    cursor: pointer;
    display: block;
    width: 100%;
    height: 100%;
    ${({ $shouldGiveRangeForMobileSize, theme }) => {
        if ($shouldGiveRangeForMobileSize) {
            return css`
                @media (max-width: ${theme.breakpoints.tabletNarrowWidth}) {
                    height: 136px;
                    width: 136px;
                }
                @media (max-width: ${theme.breakpoints.mobileWidth}) {
                    height: 116px;
                    width: 116px;
                    min-width: 116px;
                    min-height: 116px;
                }
            `;
        }
    }}
`;

const StyledImage = styled.img<{ $useInFluidGrid?: boolean }>`
    object-fit: contain;
    max-height: 100%;
    max-width: 100%;
    height: 100%;
    width: 100%;
    ${({ $useInFluidGrid }) =>
        $useInFluidGrid &&
        css`
            inset: 0;
            position: absolute;
            margin: auto;
        `}
`;

const ItemCardImageContainer = styled.div<{
    $dimension: number;
    $shouldGiveRangeForMobileSize?: boolean;
    $useInFluidGrid?: boolean;
}>`
    ${({ $dimension }) => {
        if (!$dimension) {
            return css`
                height: 100%;
                width: 100%;
            `;
        } else {
            return css`
                height: $dimension + 'px';
                width: $dimension + 'px';
            `;
        }
    }}
    display: flex;
    align-items: center;
    justify-content: center;

    ${({ theme }) => css`
        @media (max-width: ${theme.breakpoints.tabletNarrowWidth}) {
            height: 100%;
            width: 100%;
        }
    `}
    ${({ $shouldGiveRangeForMobileSize, theme }) =>
        $shouldGiveRangeForMobileSize &&
        css`
            @media (max-width: ${theme.breakpoints.tabletNarrowWidth}) {
                height: 136px;
                width: 136px;
            }
            @media (max-width: ${theme.breakpoints.mobileWidth}) {
                height: 116px;
                width: 116px;
                min-width: 116px;
                min-height: 116px;
            }
        `}
        ${({ $useInFluidGrid, theme }) =>
        $useInFluidGrid &&
        css`
            @media (max-width: ${theme.breakpoints.tabletNarrowWidth}) {
                display: flex;
                align-items: center;
                justify-content: space-between;
                flex-direction: column-reverse;
            }
        `}
`;
